import { type Profile, type ProfilePrivate, type Session, api } from '@here/api'
import * as Sentry from '@sentry/solid'
import { createEffect, createResource, createSignal } from 'solid-js'
import { getProfilePicture } from './getProfilePicture'
import { loginUserThirdParty } from '@core'

export const [user, setUser] = createSignal<Session>()
export const isAuthenticated = () => !!user()?.access_token
export const userId = () => user()?.user.id

export function useAuth() {
	const [profile, setProfile] = createSignal<Profile>()
	const [profilePrivate, setProfilePrivate] = createSignal<ProfilePrivate>()

	const [upsertProfile, setUpsertProfile] = createSignal<Profile>()
	const [upsertProfileResource] = createResource(upsertProfile, setUpsertProfile)

	const [profileResource, { refetch: refetchProfile }] = createResource(
		user,
		async (currentUser) => {
			if (currentUser) {
				const { data: publicProfile, error } = await api()
					.from('profile')
					.select('*')
					.eq('id', currentUser.user.id)
					.single()

				const { data: privateProfile, error: privateProfileError } = await api()
					.from('profile_private')
					.select('*')
					.eq('id', currentUser.user.id)
					.single()
          try {
            loginUserThirdParty(
              currentUser.user.id,
              currentUser.user.email
            )
          } catch (error) {
            console.error('error setting user context for third parties: ',  error)
          }
				return { privateProfile, publicProfile }
			}
		},
	)

	const username = () => profile()?.username
	const isRecruit = () => profilePrivate()?.role === 'recruit'
	const isRifleman = () => profilePrivate()?.role === 'rifleman'
	const isCorporal = () => profilePrivate()?.role === 'corporal'
	const isSergeant = () => profilePrivate()?.role === 'sergeant'
	const isAdmin = () => profilePrivate()?.role === 'admin'
	const isCommander = () => profilePrivate()?.role === 'commander'
  const isSuper = () => isAdmin() || isCommander()
	const isOnboarded = () => username() && profilePictureResource()
	const canSignalPhq = () => isCommander() || isAdmin() || profilePrivate()?.is_top_gun

	const logout = () => {
		api().auth.signOut()
		Sentry.setUser(null)
		localStorage.removeItem('sb-localhost-auth-token')
		setUser()
	}

	const [profilePictureResource] = createResource(userId, getProfilePicture)

	createEffect(() => {
		const res = profileResource()
		res?.publicProfile && setProfile(res.publicProfile)
		res?.privateProfile && setProfilePrivate(res.privateProfile)
	})

	return {
		canSignalPhq,
		isAdmin,
    isSuper,
		isCommander,
		isCorporal,
		isOnboarded,
		isRecruit,
		isRifleman,
		isSergeant,
		logout,
		profile,
		profilePictureResource,
		refetchProfile,
		setProfile,
		setUpsertProfile,
		upsertProfileResource,
		username,
    profilePrivate
	}
}
